import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  Typography,
  Paper,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('email'); // 'email' | 'success'
  const [authCode, setAuthCode] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/register-code', { email });

      if (response.data.success) {
        setAuthCode(response.data.data.authCode);
        setStep('success');
      } else {
        setError(response.data.error || '회원가입에 실패했습니다.');
      }
    } catch (err) {
      const message = err.response?.data?.error || '회원가입에 실패했습니다.';
      setError(message);
    }

    setIsLoading(false);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(authCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const textArea = document.createElement('textarea');
      textArea.value = authCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  // Step 1: 이메일 입력
  if (step === 'email') {
    return (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          이메일 인증코드 회원가입
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          이메일 주소만으로 간편하게 가입하세요
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: '#e8f4fd',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
          <Typography variant="body2" sx={{ textAlign: 'left' }}>
            이메일 주소로 128자 인증코드를 받아 로그인하는 방식입니다. 비밀번호 없이 안전하게 이용하세요!
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            placeholder="work@gmail.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            required
            autoFocus
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
              },
            }}
          >
            {isLoading ? '처리 중...' : '인증코드 받기'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary">
          이미 계정이 있으신가요?{' '}
          <Link component={RouterLink} to="/">
            로그인하기
          </Link>
        </Typography>
      </Box>
    );
  }

  // Step 2: 인증코드 발급 완료
  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        회원가입 완료!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        이메일로 인증코드가 발송되었습니다
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: '#e8f5e9',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            회원가입이 성공적으로 완료되었습니다!
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          아래 128자 인증코드를 복사하여 로그인하세요.
        </Typography>
      </Paper>

      <Box sx={{ textAlign: 'left', mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          인증코드:
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            backgroundColor: '#fafafa',
          }}
        >
          {authCode}
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyCode}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
            },
          }}
        >
          {copied ? '복사됨!' : '인증코드 복사'}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleGoToLogin}
        >
          인증코드 로그인
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary">
        이미 계정이 있으신가요?{' '}
        <Link component={RouterLink} to="/">
          로그인하기
        </Link>
      </Typography>
    </Box>
  );
}
